import React, { useState, useEffect, useRef } from 'react';
import './ImageSlider.css';

interface ImageSliderProps {
  images: string[];
}

export default function ImageSlider({ images }: ImageSliderProps) {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  useEffect(() => {
    if (!images || images.length <= 1) return;
    const interval = setInterval(() => {
      setCurrent(prev => (prev + 1) % images.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [images]);

  const prevSlide = () => {
    if (!images || images.length === 0) return;
    setCurrent(prev => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const nextSlide = () => {
    if (!images || images.length === 0) return;
    setCurrent(prev => (prev + 1) % images.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const diff = touchStartX.current - touchEndX.current;
    if (diff > 50) {
      nextSlide();
    } else if (diff < -50) {
      prevSlide();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  if (!images || images.length === 0) return null;

  return (
    <div 
      className="image-slider"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {images.map((mediaUrl, idx) => {
        const isVideo = typeof mediaUrl === 'string' && 
          (mediaUrl.endsWith('.webm') || mediaUrl.endsWith('.mp4') || mediaUrl.includes('.webm?') || mediaUrl.includes('.mp4?'));

        return (
          <div 
            key={idx} 
            className={`slider-slide ${idx === current ? 'active' : ''}`}
          >
            {isVideo ? (
              <video
                src={mediaUrl}
                autoPlay
                loop
                muted
                playsInline
                className="slider-video"
              />
            ) : (
              <img
                src={mediaUrl}
                alt={`Festive Banner ${idx + 1}`}
                className="slider-img"
              />
            )}
          </div>
        );
      })}

      {images.length > 1 && (
        <>
          <button 
            className="slider-arrow left" 
            onClick={prevSlide}
            aria-label="Previous slide"
          >
            &#8249;
          </button>
          <button 
            className="slider-arrow right" 
            onClick={nextSlide}
            aria-label="Next slide"
          >
            &#8250;
          </button>
          <div className="slider-dots">
            {images.map((_, idx) => (
              <button
                key={idx}
                type="button"
                className={`dot ${idx === current ? 'active' : ''}`}
                onClick={() => setCurrent(idx)}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}